import type { Target, TargetAndTransition } from "framer-motion";

export type SpeedCurve =
  | "ease"
  | "ease-in"
  | "ease-out"
  | "ease-in-out"
  | "linear"
  | "step-start"
  | "step-end"
  | `steps(${string})`
  | `cubic-bezier(${string})`;

export type Animation = {
  initial: Target;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
};

const reducedMotionAnimation: Animation = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      ease: "linear",
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0,
    },
  },
};

const fadeInDownAnmation: Animation = {
  initial: {
    height: 0,
    opacity: 0,
    y: -10,
  },
  animate: {
    height: "auto",
    opacity: 1,
    y: 0,
    transition: {
      ease: "linear",
      duration: 0.4,
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    y: -10,
    transition: {
      delay: 0,
      duration: 0.3,
    },
  },
};

const putDownAnimation: Animation = {
  initial: {
    height: "auto",
    opacity: 0,
    y: 50,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      ease: "easeInOut",
      duration: 0.3,
      delay: 0.07,
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    y: 50,
    transition: {
      delay: 0,
      duration: 0.3,
    },
  },
};

const throwInAnimation: Animation = {
  initial: {
    height: "auto",
    opacity: 0,
    x: 100,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.07,
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    x: 100,
  },
};

const fadeInAnimation: Animation = {
  initial: {
    opacity: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      ease: "linear",
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const fadeOutAnimation: Animation = {
  initial: {
    opacity: 1,
  },
  animate: {
    opacity: 0,
    transition: {
      ease: "linear",
      duration: 0.4,
    },
  },
  exit: {},
};

export const animationDuration = {
  fast: 0.2,
  medium: 0.4,
};

export const animations = {
  "fade-in": fadeInAnimation,
  "fade-in-down": fadeInDownAnmation,
  "fade-out": fadeOutAnimation,
  "reduced-motion": reducedMotionAnimation,
  "put-down": putDownAnimation,
  "throw-in": throwInAnimation,
};

export type AnimationType = keyof typeof animations;

export const getAnimation = (type: AnimationType): Animation =>
  animations[type];
